import React from "react";
import { ArrowRight, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DashboardSparkline } from "@/components/dashboard/KpiSparkline";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const TONES = {
  danger: { text: "text-danger", stroke: "#EF4444", border: "border-danger/25 hover:border-danger/50", glow: "from-danger/[0.07]" },
  success: { text: "text-success", stroke: "#22C55E", border: "border-success/25 hover:border-success/50", glow: "from-success/[0.06]" },
  warning: { text: "text-warning", stroke: "#F59E0B", border: "border-warning/25 hover:border-warning/50", glow: "from-warning/[0.06]" },
  info: { text: "text-info", stroke: "#3B82F6", border: "border-info/25 hover:border-info/50", glow: "from-info/[0.06]" },
  neutral: { text: "text-foreground/80", stroke: "#A1A1AA", border: "border-border hover:border-foreground/30", glow: "from-foreground/[0.03]" },
  emerald: { text: "text-emerald-600 dark:text-emerald-400", stroke: "#10B981", border: "border-emerald-500/25 hover:border-emerald-500/50", glow: "from-emerald-500/[0.06]" },
  teal: { text: "text-teal-600 dark:text-teal-400", stroke: "#14B8A6", border: "border-teal-500/25 hover:border-teal-500/50", glow: "from-teal-500/[0.06]" },
};

/**
 * KpiCard — dashboard KPI card with real-data sparkline.
 * spark: [{d, v}] or null. When null / all-zero → neutral flat state (no fabricated trend).
 * compact: denser variant so 6 cards fit on one desktop/laptop row.
 */
export default function KpiCard({ label, value, subLines = [], icon: Icon, tone = "info", spark, metric, rangeKey, tooltip, onClick, testid, compact = false }) {
  const t = TONES[tone] || TONES.info;
  const hasTrend = Array.isArray(spark) && spark.some((p) => p.v > 0);

  return (
    <button
      onClick={onClick}
      data-testid={testid}
      aria-label={`${label} : ${value}`}
      className={cn(
        "group relative text-left rounded-2xl border bg-card transition-all duration-200 overflow-hidden min-w-0",
        compact ? "p-3" : "p-4 md:p-5",
        "bg-gradient-to-br to-transparent hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_rgba(15,23,42,0.06)] dark:shadow-none",
        t.border, t.glow
      )}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className={cn("flex items-center gap-1.5 uppercase tracking-[0.12em] font-semibold text-muted-foreground min-w-0",
          compact ? "text-[9.5px]" : "text-[10px] md:text-[11px] gap-2 tracking-[0.14em]")}>
          {Icon && <Icon className={cn("shrink-0", compact ? "h-3 w-3" : "h-3.5 w-3.5", t.text)} />}
          <span className="truncate">{label}</span>
        </div>
        {tooltip && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground/40 hover:text-muted-foreground cursor-help shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Info className="h-3 w-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[240px] text-xs">{tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <div className={cn("flex items-end justify-between gap-2 min-w-0", compact ? "mt-1.5" : "mt-2.5")}>
        <div className={cn("leading-none font-semibold tabular-nums tracking-tight truncate",
          compact ? "text-[28px] md:text-[32px]" : "text-4xl md:text-[44px]", t.text)} data-testid={`${testid}-value`}>
          {value}
        </div>
        <ArrowRight className={cn("mb-1 text-muted-foreground/40 group-hover:text-foreground/80 group-hover:translate-x-0.5 transition-all shrink-0",
          compact ? "h-3.5 w-3.5" : "h-4 w-4")} />
      </div>

      <div className={cn("space-y-0.5 min-w-0", compact ? "mt-1 min-h-[26px]" : "mt-2 min-h-[28px]")}>
        {subLines.filter(Boolean).map((s, i) => (
          <div key={i} className={cn("text-muted-foreground leading-snug truncate", compact ? "text-[10.5px]" : "text-[11px]")}>{s}</div>
        ))}
      </div>

      {/* Sparkline — real data only */}
      <div className={cn("-mx-1", compact ? "mt-1.5 h-7 min-h-[28px]" : "mt-2 h-10 min-h-[40px]")} style={{ minWidth: 0 }}>
        {hasTrend ? (
          <DashboardSparkline
            data={spark}
            metric={metric}
            stroke={t.stroke}
            gradientId={`spark-${testid}`}
            rangeKey={rangeKey}
            label={label}
          />
        ) : (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="h-full flex items-center px-1" data-testid={`${testid}-flat`}>
                  <div className="w-full border-t border-dashed border-border" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">{tfr("Pas assez de données historiques")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </button>
  );
}
